import { ReactNode, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import SetupChecklist from './SetupChecklist';
import SupportAccessBanner from './SupportAccessBanner';
import HelpChat from '../help/HelpChat';
import CommandPalette from './CommandPalette';
import { useSetupProgress } from '../../hooks/useSetupProgress';

/**
 * First-run redirect: a brand-new org (no KB docs, no flows — derived from
 * setup-progress, not stored flags) lands in the onboarding wizard once.
 * Skip/completion state lives server-side on the Organization
 * (setup-progress → onboarding.{completed_at,skipped_at}), so the redirect
 * behaves the same across browsers and devices. localStorage is kept only as
 * a fast-path cache to avoid a redirect flash before the query resolves.
 * (onboarding-wizard-plan.md PR 4)
 */
function useFirstRunRedirect(enabled: boolean) {
  const navigate = useNavigate();
  const { data } = useSetupProgress();
  const redirected = useRef(false);

  useEffect(() => {
    if (!enabled || redirected.current || !data) return;
    // Fast-path cache (this browser already skipped/finished)
    if (localStorage.getItem('sg_onboarding_skipped') || localStorage.getItem('sg_onboarding_done')) return;
    // Server-side source of truth (any browser/device)
    if (data.onboarding?.completed_at || data.onboarding?.skipped_at) return;

    const incomplete = (id: string) => data.steps.find((s) => s.id === id)?.complete === false;
    if (incomplete('add-kb-article') && incomplete('create-flow')) {
      redirected.current = true;
      navigate('/onboarding', { replace: true });
    }
  }, [enabled, data, navigate]);
}

export default function AppShell({ children }: { children: ReactNode }) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const isOnboarding = location.pathname.startsWith('/onboarding');
  useFirstRunRedirect(!isOnboarding);

  // ⌘K / Ctrl+K opens the command palette
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    }
    function onCustom() {
      setSearchOpen(true);
    }
    window.addEventListener('keydown', onKey);
    document.addEventListener('open-search', onCustom);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('open-search', onCustom);
    };
  }, []);

  // The onboarding wizard renders full-screen without app chrome
  if (isOnboarding) {
    return <>{children}</>;
  }

  return (
    <div className="flex h-screen bg-bg overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <SupportAccessBanner />
        <TopBar />
        <SetupChecklist />
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>

      {/* Help slide-out panel */}
      {helpOpen && (
        <>
          {/* Backdrop (mobile) */}
          <div
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm md:hidden"
            onClick={() => setHelpOpen(false)}
          />
          {/* Panel */}
          <div className="fixed right-0 top-0 bottom-0 z-50 w-80 shadow-2xl border-l border-border animate-in slide-in-from-right duration-200">
            <HelpChat onClose={() => setHelpOpen(false)} />
          </div>
        </>
      )}

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Floating help button */}
      <button
        onClick={() => setHelpOpen((o) => !o)}
        className={cn(
          'fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95',
          helpOpen
            ? 'bg-surface border border-border text-text-2 hover:text-text'
            : 'bg-accent text-white hover:bg-accent-hover',
        )}
        title={helpOpen ? 'Close help' : 'Get help'}
      >
        {helpOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <HelpCircle className="w-5 h-5" />
        )}
      </button>
    </div>
  );
}
